// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/react";
import { Columns } from "@fullscript/aviary-web";
import type { MutableRefObject, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary";
import type { Sizes } from "@aviary/components/Columns/grid";
import { CatalogEmptyState } from "@shared/components/CatalogEmptyState/CatalogEmptyState";
import { DesktopFilterColumn } from "@shared/components/CatalogSearch/DesktopFilterColumn/DesktopFilterColumn";
import { MegaMenuFilterTags } from "@shared/components/CatalogSearch/MegaMenuFilterTags/MegaMenuFilterTags";
import { CatalogSortDropdown } from "@shared/components/CatalogSortDropdown/CatalogSortDropdown";
import { ProductResultsHeader } from "@shared/components/ProductResultsHeader/ProductResultsHeader";
import { SkeletonProductCard } from "@shared/components/SkeletonProductCards/SkeletonProductCard/SkeletonProductCard";
import { StickyProductResultsHeader } from "@shared/components/StickyProductResultsHeader/StickyProductResultsHeader";
import type { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogDrawerColumnWidth } from "@shared/hooks/useCatalogDrawerColumnWidth/useCatalogDrawerColumnWidth";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { isFullscriptJsVar } from "@shared/reactiveVar/useIsFullscriptJs/useIsFullscriptJs.store";
import { isInOfficeCheckoutVar } from "@shared/reactiveVar/useIsInOfficeCheckoutVar/useIsInOfficeCheckoutVar.store";

import { MobileFilterDrawer } from "../CatalogSearch/MobileFilterDrawer/MobileFilterDrawer";
import { ClickToScrollUp } from "../ClickToScrollUp/ClickToScrollUp";

interface Props {
  children: ReactNode;
  displayedCount: number;
  isLoadingMoreResults?: boolean;
  isLoadingNewResults?: boolean;
  resultCount: number;
  scrollingRef?: MutableRefObject<any>;
  title: string | (ReactElement | string)[];
  overrideEmptyStateMessage?: string;
  overrideEmptyStateHeader?: string;
  onClearAll?: () => void;
  onClearFilters: () => void;
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForLogging?: AnalyticsEvent;
  isDrawer?: boolean;
  isCyberMondayEnabled?: boolean;
  onLoadMore?: () => void;
  isCareCenter?: boolean;
  stickyHeaderStyle?: SerializedStyles;
  isFS2Wholesale?: boolean;
}

const ProductResults = ({
  children,
  displayedCount,
  isLoadingMoreResults,
  isLoadingNewResults,
  resultCount,
  scrollingRef,
  entryPointForLogging,
  eventNameForLogging,
  isDrawer = false,
  isCyberMondayEnabled = false,
  onLoadMore,
  title,
  overrideEmptyStateMessage,
  overrideEmptyStateHeader,
  onClearAll,
  onClearFilters,
  isCareCenter = false,
  stickyHeaderStyle,
  isFS2Wholesale = false,
}: Props) => {
  const { t } = useTranslation("common");
  const { activeSearchType } = useCatalogSearch();
  const { isEmerson } = useSharedGlobalConfig();
  const { tablet } = useBreakpoints();
  const isSortViewSelectorDisabled = !resultCount || resultCount <= 1;
  const isFiltersActive: boolean = activeSearchType === "filters";
  const isInOfficeCheckout = isInOfficeCheckoutVar();
  const isFullscriptJs = isFullscriptJsVar();
  const isFSJSorInOffice = isInOfficeCheckout || isFullscriptJs;
  const columnWidth = useCatalogDrawerColumnWidth(
    activeSearchType === "filters",
    undefined,
    !isFSJSorInOffice
  );

  const gridCardColumnWidth = (): Sizes => {
    if (tablet.active) {
      return 6 as Sizes;
    }

    if (isEmerson) {
      return isFiltersActive ? (4 as Sizes) : (3 as Sizes);
    }

    if (!isFSJSorInOffice) {
      return columnWidth;
    }

    return isFiltersActive ? (6 as Sizes) : (4 as Sizes);
  };

  const renderSkeletonCatalogProductCards = Array.from({
    length: !isFSJSorInOffice ? 8 : 6,
  }).map((_, i) => <SkeletonProductCard gridColumnWidth={gridCardColumnWidth()} key={i} />);

  const renderResults = () => {
    if (!isLoadingNewResults && displayedCount === 0) {
      return (
        <CatalogEmptyState
          onClearAll={onClearAll}
          overrideHeader={overrideEmptyStateHeader}
          overrideMessage={overrideEmptyStateMessage}
        />
      );
    }

    if (isLoadingNewResults) {
      return renderSkeletonCatalogProductCards;
    }

    return children;
  };

  const renderLoadMore = !isLoadingNewResults && onLoadMore && (
    <Columns.Column key="load-more-button" columnWidth={12}>
      <Button onClick={onLoadMore} isLoading={isLoadingMoreResults} isFullWidth>
        {t("LoadMore")}
      </Button>
    </Columns.Column>
  );

  const renderHeader = () => {
    if (!isFSJSorInOffice) {
      return null;
    }

    return (
      <>
        <ProductResultsHeader
          title={title}
          resultCount={resultCount}
          isLoadingNewResults={isLoadingNewResults}
          isDrawer={isDrawer}
          isCareCenter={isCareCenter}
          onClearFilters={onClearFilters}
          isFS2Wholesale={isFS2Wholesale}
        />
        <StickyProductResultsHeader
          title={title}
          scrollingRef={scrollingRef}
          resultCount={resultCount}
          displayedCount={displayedCount}
          isLoadingNewResults={isLoadingNewResults}
          isDrawer={isDrawer}
          isCareCenter={isCareCenter}
          hideGridSelector={isEmerson}
          headerStyle={stickyHeaderStyle}
        >
          <CatalogSortDropdown isDisabled={isSortViewSelectorDisabled} />
        </StickyProductResultsHeader>
      </>
    );
  };

  const renderMegaMenuHeader = () => {
    if (!isFSJSorInOffice) {
      return (
        <MegaMenuFilterTags
          onClearFilters={onClearFilters}
          resultCount={resultCount}
          resultCountDescription={title}
        />
      );
    }

    return null;
  };

  return (
    <>
      {renderHeader()}
      <DesktopFilterColumn
        isCyberMondayEnabled={isCyberMondayEnabled}
        entryPointForLogging={entryPointForLogging}
        eventNameForLogging={eventNameForLogging}
        isDrawer={isDrawer}
        filterTilesWithSpacing={!isEmerson}
        narrowFilters={!isFSJSorInOffice}
      >
        {renderMegaMenuHeader()}
        <Columns>
          {renderResults()}
          {renderLoadMore}
        </Columns>
      </DesktopFilterColumn>
      <ClickToScrollUp scrollingRef={scrollingRef} />
      <MobileFilterDrawer
        resultCount={resultCount}
        onClearFilters={onClearFilters}
        entryPointForLogging={entryPointForLogging}
        eventNameForLogging={eventNameForLogging}
        isCyberMondayEnabled={isCyberMondayEnabled}
        showClearFilterButton={isEmerson}
        titleType={isEmerson ? "h3" : "h2"}
        isLoading={isLoadingNewResults ?? false}
      />
    </>
  );
};

export { ProductResults };
